import React, { useCallback, useEffect, useRef, useState } from 'react';
import useComponentVisible from '../../../hooks/useComponentVisible';
import { getText } from '../../../utils/getter';
import './select-box.css';

interface Option {
  label: string;
  value: string | number | boolean | undefined;
}

interface SelectProps {
  initialValue?: string | number | boolean | undefined | any;
  validate?: number;
  name?: string;
  label?: string;
  extraLabel?: string;
  options: Option[];
  errorMessage?: string;
  hasError?: boolean;
  placeholder?: string;
  setValueCallBack?: (value: any) => void;
  dataTestId?: string;
  className?: string;
}

const SelectBox = React.forwardRef<any, SelectProps>(
  (
    {
      validate,
      initialValue,
      setValueCallBack,
      options,
      name,
      label,
      hasError,
      extraLabel,
      className,
      errorMessage = getText('default_option'),
      placeholder = 'Bitte auswählen',
      dataTestId = 'select',
    }: SelectProps,
    ref,
  ) => {
    const {
      triggerRef,
      componentRef,
      isComponentVisible,
      setIsComponentVisible,
    } = useComponentVisible<HTMLInputElement>(false);

    const valueObject = options.find((option) => option.value === initialValue);

    const list = useRef<HTMLUListElement>(null);

    const [value, setValue] = useState<Option | undefined>(undefined);

    const [fakeValue, setFakeValue] = useState<string | undefined>('');

    const [selectHighlight, setSelectHighlight] = useState<number | null>(null);

    const [validationError, setValidationError] = useState(false);

    const [initialOptions, setInitialOptions] = useState(options);

    const onChange = (e: React.FormEvent<HTMLInputElement>) => {
      setFakeValue(e.currentTarget.value);
      if (!options.find((option) => option.label === e.currentTarget.value)) {
        setValidationError(true);
      }
      setIsComponentVisible(true);
      setInitialOptions(
        options.filter((option) =>
          option.label
            .toLocaleLowerCase()
            .includes(e.currentTarget.value.toLocaleLowerCase()),
        ),
      );
      // clear highlight when filtering
      setSelectHighlight(null);
    };

    // shared selection logic for both click and keyboard
    const selectOption = useCallback(
      (selectedValue: Option) => {
        if (setValueCallBack) {
          setValueCallBack(selectedValue.value);
        }
        setFakeValue(selectedValue.label);
        setValidationError(false);
        setInitialOptions(options);
        setValue(selectedValue);
        setIsComponentVisible(false);
      },
      [options, setIsComponentVisible, setValueCallBack],
    );

    const memoizedKeyDown = useCallback(
      ({ key }: KeyboardEvent) => {
        if (!isComponentVisible) return;
        // navigate filtered list
        const maxIndex = initialOptions.length - 1;
        if (key === 'ArrowDown') {
          if (selectHighlight === null) {
            setSelectHighlight(0);
          } else if (selectHighlight >= maxIndex) {
            setSelectHighlight(maxIndex);
          } else {
            setSelectHighlight(selectHighlight + 1);
            list?.current?.scroll({
              top: selectHighlight * 60,
              behavior: 'smooth',
            });
          }
        }
        if (key === 'ArrowUp') {
          if (selectHighlight === null) {
            setSelectHighlight(maxIndex);
          } else if (selectHighlight === 0) {
            setSelectHighlight(0);
          } else {
            setSelectHighlight(selectHighlight - 1);
            list?.current?.scroll({
              top: (selectHighlight - 1) * 60,
              behavior: 'smooth',
            });
          }
        }
        // select with Enter/Space from filtered options
        if ((key === ' ' || key === 'Enter') && selectHighlight !== null) {
          selectOption(initialOptions[selectHighlight]);
        }
      },
      [initialOptions, selectHighlight, selectOption, isComponentVisible],
    );

    useEffect(() => {
      window.addEventListener('keydown', memoizedKeyDown);

      return () => {
        window.removeEventListener('keydown', memoizedKeyDown);
      };
    }, [selectHighlight, memoizedKeyDown]);

    useEffect(() => {
      if (validate && (!fakeValue || !value)) {
        setValidationError(true);
      }
    }, [value, validate, fakeValue]);

    useEffect(() => {
      if (valueObject) {
        setValue(valueObject);
        setFakeValue(valueObject.label);
      }
    }, [setValue, valueObject]);

    useEffect(() => {
      if (hasError !== undefined) {
        setValidationError(hasError);
      }
    }, [hasError]);

    // Dynamic Option Re-Render
    useEffect(() => {
      if (options) {
        setInitialOptions(options);
        // clear any previous highlight when options reset!
        setSelectHighlight(null);
      }
    }, [options]);

    const maxHeight = options.length * 60 > 240 ? 240 : options.length * 60;
    return (
      <div className='select-box-main'>
        <div className='label'>
          <label className='content-normal'>{label}</label>
          {extraLabel && (
            <span className='small-text light-grey'>{extraLabel}</span>
          )}
        </div>
        <div className='select-box-container'>
          <div
            role='presentation'
            data-testid={dataTestId}
            className={`select-box ${isComponentVisible ? 'focused' : ''} ${
              validationError ? 'box-input-error' : ' '
            } ${className || ''}`}
          >
            <input
              data-testid={`select-input-${name}`}
              type='hidden'
              name={name}
              ref={ref}
            />
            <input
              ref={triggerRef}
              data-testid='select-fake-input'
              type='text'
              value={fakeValue}
              className='input-text'
              placeholder={placeholder}
              onChange={onChange}
              onKeyDown={(e) => {
                if (
                  (e.key === 'Enter' || e.key === ' ') &&
                  selectHighlight !== null
                ) {
                  // avoids form execution
                  e.preventDefault();
                  selectOption(initialOptions[selectHighlight]);
                }
              }}
            />
            <div ref={componentRef}>
              <ul
                data-testid='select-list'
                ref={list}
                className={`options ${
                  isComponentVisible ? 'visible' : 'hidden'
                } `}
                style={{ maxHeight }}
              >
                {initialOptions.map((option, index) => (
                  <li
                    role='presentation'
                    key={index}
                    className={`input-text ${
                      selectHighlight === index ? 'highlighted' : ''
                    }`}
                    onMouseEnter={() => setSelectHighlight(index)}
                    onClick={() => selectOption(option)}
                  >
                    {option.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {validationError && (
            <p className='error small-text left select-box-error'>
              {errorMessage}
            </p>
          )}
        </div>
      </div>
    );
  },
);

export default SelectBox;
